import React, { useState, useMemo, useEffect, useRef } from 'react';
import RoleBadge from './RoleBadge';
import FilterBar from './FilterBar';
import { Trash2, ChevronDown, ChevronUp, Search, Award, Users, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const UserTable = ({ users, updateUserRole, deleteUser, updateStudentMatches, refreshUsers }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [badges, setBadges] = useState([]);
  const [userBadges, setUserBadges] = useState({}); // userId -> array of badges
  const [userReferrals, setUserReferrals] = useState({}); // userId -> array of referrals
  const [awardingBadge, setAwardingBadge] = useState(null); // { userId, badgeId } when awarding
  const [selectedUsers, setSelectedUsers] = useState([]); // Array of selected user IDs
  const [bulkBadgeDropdownOpen, setBulkBadgeDropdownOpen] = useState(false);
  const [bulkMatchesValue, setBulkMatchesValue] = useState('');
  const [sortField, setSortField] = useState(null); // 'createdAt' or 'lastLoginAt'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const badgeDropdownRefs = useRef({});
  const bulkBadgeRef = useRef(null);

  useEffect(() => {
    fetchBadges();
  }, []);

  useEffect(() => {
    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (awardingBadge) {
        const ref = badgeDropdownRefs.current[awardingBadge.userId];
        if (ref && !ref.contains(event.target)) {
          setAwardingBadge(null);
        }
      }
      if (bulkBadgeDropdownOpen && bulkBadgeRef.current && !bulkBadgeRef.current.contains(event.target)) {
        setBulkBadgeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [awardingBadge, bulkBadgeDropdownOpen]);

  const fetchBadges = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges`);
      const data = await response.json();
      setBadges(data);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const fetchUserBadges = async (userId, forceRefresh = false) => {
    if (!forceRefresh && userBadges[userId]) return; // Already fetched, unless forcing refresh
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/user/${userId}`);
      const data = await response.json();
      setUserBadges(prev => ({
        ...prev,
        [userId]: data
      }));
    } catch (error) {
      console.error('Error fetching user badges:', error);
      setUserBadges(prev => ({
        ...prev,
        [userId]: []
      }));
    }
  };

  const fetchUserReferrals = async (userId, forceRefresh = false) => {
    if (!forceRefresh && userReferrals[userId] !== undefined) return; // Already fetched, unless forcing refresh
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUserReferrals(prev => ({
        ...prev,
        [userId]: data || []
      }));
    } catch (error) {
      console.error('Error fetching user referrals:', error);
      setUserReferrals(prev => ({
        ...prev,
        [userId]: []
      }));
    }
  };

  const toggleRow = (userId) => {
    setExpandedRow(expandedRow === userId ? null : userId);
    if (expandedRow !== userId) {
      fetchUserBadges(userId);
      // Only fetch referrals for students
      const user = users.find(u => u._id === userId);
      if (user && user.role === 'student') {
        fetchUserReferrals(userId);
      }
    }
  };

  const formatDate = (date) => (!date ? 'N/A' : new Date(date).toLocaleDateString());
  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const dateObj = new Date(date);
    return dateObj.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  const formatTestScore = (score) => (score || score === 0 ? score : 'N/A');

  // Helper function to check if a value is within range
  const isInRange = (value, min, max) => {
    if (!min && !max) return true;
    if (!min) return value <= max;
    if (!max) return value >= min;
    return value >= min && value <= max;
  };

  // Helper function to check if arrays have intersection
  const hasIntersection = (userArray, filterArray) => {
    if (!filterArray || filterArray.length === 0) return true;
    if (!userArray || userArray.length === 0) return false;
    return userArray.some(item => filterArray.includes(item));
  };

  // Helper function to check date range
  const isDateInRange = (dateString, startDate, endDate) => {
    if (!startDate && !endDate) return true;
    if (!dateString) return false;
    
    const date = new Date(dateString);
    if (!startDate) return date <= new Date(endDate);
    if (!endDate) return date >= new Date(startDate);
    return date >= new Date(startDate) && date <= new Date(endDate);
  };

  // 🔍 Comprehensive filtering logic
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Text search filter
    const query = searchQuery.toLowerCase().trim();
    if (query) {
      filtered = filtered.filter((user) =>
        [user.name, user.email, user.role]
          .filter(Boolean)
          .some((val) => val.toLowerCase().includes(query))
      );
    }

    // Apply advanced filters
    filtered = filtered.filter((user) => {
      // Basic filters (now arrays)
      if (filters.role && filters.role.length > 0 && !filters.role.includes(user.role)) return false;
      // Gender stored in lowercase in DB, filter also stores lowercase (empty string for "Prefer not to say")
      if (filters.gender && filters.gender.length > 0) {
        const userGender = user.gender || ''; // Normalize undefined/null to empty string
        if (!filters.gender.includes(userGender)) return false;
      }
      if (filters.citizenship && filters.citizenship.length > 0 && !filters.citizenship.includes(user.citizenship)) return false;
      if (filters.firstLanguage && filters.firstLanguage.length > 0 && !filters.firstLanguage.includes(user.firstLanguage)) return false;
      
      // Profile completion filter (now array)
      if (filters.profileCompleted && filters.profileCompleted.length > 0) {
        const isCompleted = user.profileCompleted === true;
        const wantsCompleted = filters.profileCompleted.includes('true');
        const wantsIncomplete = filters.profileCompleted.includes('false');
        // If both selected, match all; if only one selected, filter accordingly
        if (wantsCompleted && !wantsIncomplete && !isCompleted) return false;
        if (wantsIncomplete && !wantsCompleted && isCompleted) return false;
      }

      // Array filters
      if (filters.fields && filters.fields.length > 0 && !hasIntersection(user.fields, filters.fields)) return false;
      if (filters.interests && filters.interests.length > 0 && !hasIntersection(user.interests, filters.interests)) return false;
      if (filters.preferredCountries && filters.preferredCountries.length > 0 && !hasIntersection(user.preferredCountries, filters.preferredCountries)) return false;

      // Test score range filters
      if (filters.greVerbal && !isInRange(user.greVerbal, filters.greVerbal.min, filters.greVerbal.max)) return false;
      if (filters.greQuantitative && !isInRange(user.greQuantitative, filters.greQuantitative.min, filters.greQuantitative.max)) return false;
      if (filters.greWriting && !isInRange(user.greWriting, filters.greWriting.min, filters.greWriting.max)) return false;
      
      if (filters.toeflReading && !isInRange(user.toeflReading, filters.toeflReading.min, filters.toeflReading.max)) return false;
      if (filters.toeflListening && !isInRange(user.toeflListening, filters.toeflListening.min, filters.toeflListening.max)) return false;
      if (filters.toeflSpeaking && !isInRange(user.toeflSpeaking, filters.toeflSpeaking.min, filters.toeflSpeaking.max)) return false;
      if (filters.toeflWriting && !isInRange(user.toeflWriting, filters.toeflWriting.min, filters.toeflWriting.max)) return false;
      
      if (filters.ieltsListening && !isInRange(user.ieltsListening, filters.ieltsListening.min, filters.ieltsListening.max)) return false;
      if (filters.ieltsReading && !isInRange(user.ieltsReading, filters.ieltsReading.min, filters.ieltsReading.max)) return false;
      if (filters.ieltsSpeaking && !isInRange(user.ieltsSpeaking, filters.ieltsSpeaking.min, filters.ieltsSpeaking.max)) return false;
      if (filters.ieltsWriting && !isInRange(user.ieltsWriting, filters.ieltsWriting.min, filters.ieltsWriting.max)) return false;
      if (filters.ieltsOverall && !isInRange(user.ieltsOverall, filters.ieltsOverall.min, filters.ieltsOverall.max)) return false;

      // System filters (now arrays)
      if (filters.hasResume && filters.hasResume.length > 0) {
        const hasResume = !!user.resumeKey;
        const wantsHasResume = filters.hasResume.includes('true');
        const wantsNoResume = filters.hasResume.includes('false');
        // If both selected, match all; if only one selected, filter accordingly
        if (wantsHasResume && !wantsNoResume && !hasResume) return false;
        if (wantsNoResume && !wantsHasResume && hasResume) return false;
      }

      if (filters.googleLinked && filters.googleLinked.length > 0) {
        const isGoogleLinked = !!user.googleId;
        const wantsLinked = filters.googleLinked.includes('true');
        const wantsNotLinked = filters.googleLinked.includes('false');
        // If both selected, match all; if only one selected, filter accordingly
        if (wantsLinked && !wantsNotLinked && !isGoogleLinked) return false;
        if (wantsNotLinked && !wantsLinked && isGoogleLinked) return false;
      }

      // Active users filter (same logic as analytics dashboard)
      if (filters.activeUsers && filters.activeUsers.length > 0) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const hasRecentLogin = user.lastLoginAt && new Date(user.lastLoginAt) >= thirtyDaysAgo;
        const hasMultipleLogins = user.loginHistory && Array.isArray(user.loginHistory) && user.loginHistory.length >= 2;
        const isActive = hasRecentLogin && hasMultipleLogins;
        
        const wantsActive = filters.activeUsers.includes('true');
        const wantsNotActive = filters.activeUsers.includes('false');
        // If both selected, match all; if only one selected, filter accordingly
        if (wantsActive && !wantsNotActive && !isActive) return false;
        if (wantsNotActive && !wantsActive && isActive) return false;
      }

      // Date range filters
      if (filters.dateRange && !isDateInRange(user.createdAt, filters.dateRange.start, filters.dateRange.end)) return false;
      if (filters.lastLoginRange && !isDateInRange(user.lastLoginAt, filters.lastLoginRange.start, filters.lastLoginRange.end)) return false;

      // Student-specific filters
      if (filters.matchesRemaining && !isInRange(user.studentMatchesRemaining, filters.matchesRemaining.min, filters.matchesRemaining.max)) return false;
      
      if (filters.hasMatches && filters.hasMatches.length > 0) {
        const hasMatches = user.matchedPositions && user.matchedPositions.length > 0;
        const wantsHasMatches = filters.hasMatches.includes('true');
        const wantsNoMatches = filters.hasMatches.includes('false');
        // If both selected, match all; if only one selected, filter accordingly
        if (wantsHasMatches && !wantsNoMatches && !hasMatches) return false;
        if (wantsNoMatches && !wantsHasMatches && hasMatches) return false;
      }

      // Has Referral filter
      if (filters.hasReferral && filters.hasReferral.length > 0) {
        // Get referral count - check if it exists on user object, otherwise check userReferrals
        let referralCount = user.referralCount;
        if (referralCount === undefined || referralCount === null) {
          // Fallback: check if we have referral data loaded for this user
          const userRefs = userReferrals[user._id];
          referralCount = userRefs ? userRefs.length : 0;
        }
        const hasReferrals = referralCount > 0;
        const wantsHasReferrals = filters.hasReferral.includes('true');
        const wantsNoReferrals = filters.hasReferral.includes('false');
        // If both selected, match all; if only one selected, filter accordingly
        if (wantsHasReferrals && !wantsNoReferrals && !hasReferrals) return false;
        if (wantsNoReferrals && !wantsHasReferrals && hasReferrals) return false;
      }

      return true;
    });

    // Apply sorting
    // Default sorting by referral count (most referrals first) if no sort field is selected
    if (!sortField) {
      filtered = [...filtered].sort((a, b) => {
        // Get referral count - check if it exists on user object, otherwise check userReferrals
        let aCount = a.referralCount;
        if (aCount === undefined || aCount === null) {
          const aRefs = userReferrals[a._id];
          aCount = aRefs ? aRefs.length : 0;
        }
        
        let bCount = b.referralCount;
        if (bCount === undefined || bCount === null) {
          const bRefs = userReferrals[b._id];
          bCount = bRefs ? bRefs.length : 0;
        }
        
        return bCount - aCount; // Descending order (most referrals first)
      });
    } else if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        // Special handling for lastLoginAt - users with no login history should always be at bottom
        if (sortField === 'lastLoginAt') {
          const aHasLogin = a.loginHistory && a.loginHistory.length > 0 && a.lastLoginAt;
          const bHasLogin = b.loginHistory && b.loginHistory.length > 0 && b.lastLoginAt;

          // If both have no login history, keep original order
          if (!aHasLogin && !bHasLogin) return 0;
          // If only a has no login history, put it at bottom
          if (!aHasLogin) return 1;
          // If only b has no login history, put it at bottom
          if (!bHasLogin) return -1;

          // Both have login history, sort normally by date
          const aValue = new Date(a.lastLoginAt);
          const bValue = new Date(b.lastLoginAt);

          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }

        // Standard sorting for other fields
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle null/undefined values - put them at the end
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;

        // Convert to Date objects if they're date strings
        if (sortField === 'createdAt') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        // Compare values
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [users, searchQuery, filters, sortField, sortDirection, userReferrals]);

  const awardBadgeToUser = async (userId, badgeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/award`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: userId,
          badgeId: badgeId
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Refresh user badges (force refresh to get updated list)
        fetchUserBadges(userId, true);
        alert('Badge awarded successfully!');
        setAwardingBadge(null);
      } else {
        alert(data.error || 'Error awarding badge. User may already have this badge.');
      }
    } catch (error) {
      console.error('Error awarding badge:', error);
      alert('Error awarding badge');
    }
  };

  const getBadgeIcon = (icon) => {
    switch (icon) {
      case 'star': return '⭐';
      case 'trophy': return '🏆';
      case 'medal': return '🏅';
      case 'fire': return '🔥';
      case 'heart': return '❤️';
      case 'crown': return '👑';
      case 'gem': return '💎';
      case 'rocket': return '🚀';
      case 'check': return '✅';
      default: return '🏅';
    }
  };

  // Handle user selection
  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for a column
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} className="ml-1 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp size={14} className="ml-1 text-gray-600" />
      : <ArrowDown size={14} className="ml-1 text-gray-600" />;
  };

  // Handle select all functionality (based on filtered users)
  const handleSelectAll = () => {
    const filteredUserIds = filteredUsers.map(user => user._id);
    const allSelected = filteredUserIds.length > 0 && 
                       filteredUserIds.every(id => selectedUsers.includes(id));
    
    if (allSelected) {
      // Deselect all filtered users
      setSelectedUsers(prev => prev.filter(id => !filteredUserIds.includes(id)));
    } else {
      // Select all filtered users
      setSelectedUsers(prev => {
        const newSelected = [...prev];
        filteredUserIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  // Check if all filtered users are selected
  const allFilteredUsersSelected = useMemo(() => {
    if (filteredUsers.length === 0) return false;
    const filteredUserIds = filteredUsers.map(user => user._id);
    return filteredUserIds.every(id => selectedUsers.includes(id));
  }, [filteredUsers, selectedUsers]);

  // Check if some (but not all) filtered users are selected
  const someFilteredUsersSelected = useMemo(() => {
    if (filteredUsers.length === 0) return false;
    const filteredUserIds = filteredUsers.map(user => user._id);
    const selectedCount = filteredUserIds.filter(id => selectedUsers.includes(id)).length;
    return selectedCount > 0 && selectedCount < filteredUserIds.length;
  }, [filteredUsers, selectedUsers]);

  // Get selected students only
  const getSelectedStudents = () => {
    return selectedUsers
      .map(userId => users.find(u => u._id === userId))
      .filter(user => user && user.role === 'student');
  };

  // Bulk award badge
  const handleBulkAwardBadge = async (badgeId) => {
    if (selectedUsers.length === 0 || !badgeId) return;

    try {
      const token = localStorage.getItem('token');
      const promises = selectedUsers.map(async (userId) => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/award`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: userId,
            badgeId: badgeId
          })
        });

        let errorData = null;
        if (!response.ok) {
          try {
            errorData = await response.json();
          } catch {
            errorData = { error: 'Unknown error' };
          }
        }

        return {
          userId,
          ok: response.ok,
          status: response.status,
          error: errorData?.error || null
        };
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.ok).length;
      const alreadyHasCount = results.filter(r => !r.ok && r.error && r.error.includes('already has')).length;
      const userIdsToRefresh = [...selectedUsers]; // Store before clearing
      
      // Refresh badges for all selected users (even if they already had the badge)
      userIdsToRefresh.forEach(userId => {
        if (userBadges[userId]) {
          fetchUserBadges(userId, true);
        }
      });

      if (successCount === selectedUsers.length) {
        alert(`✅ Badge awarded successfully to ${successCount} user(s)!`);
        setSelectedUsers([]);
        setBulkBadgeDropdownOpen(false);
      } else if (successCount > 0) {
        const message = alreadyHasCount > 0
          ? `Badge awarded to ${successCount} user(s). ${alreadyHasCount} user(s) already had this badge.`
          : `Badge awarded to ${successCount} out of ${selectedUsers.length} user(s).`;
        alert(`⚠️ ${message}`);
        setSelectedUsers([]);
        setBulkBadgeDropdownOpen(false);
      } else {
        // All failed - check if they all already have it
        if (alreadyHasCount === selectedUsers.length) {
          alert(`ℹ️ All ${selectedUsers.length} selected user(s) already have this badge.`);
          setSelectedUsers([]);
          setBulkBadgeDropdownOpen(false);
        } else {
          // Some other error occurred
          const failedResults = results.filter(r => !r.ok);
          const errorMessages = failedResults.map(r => r.error || `Status ${r.status}`).join(', ');
          alert(`❌ Error awarding badge: ${errorMessages}`);
        }
      }
    } catch (error) {
      console.error('Error awarding badge:', error);
      alert(`❌ Network error awarding badge: ${error.message}`);
    }
  };

  // Bulk update student matches (adds to existing total)
  const handleBulkUpdateMatches = async () => {
    const selectedStudents = getSelectedStudents();
    if (selectedStudents.length === 0 || !bulkMatchesValue) {
      alert('Please select students and enter a match value');
      return;
    }

    const matchesToAdd = parseInt(bulkMatchesValue);
    if (isNaN(matchesToAdd)) {
      alert('Please enter a valid number for matches');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const promises = selectedStudents.map(async (student) => {
        // Get current matches and add to it
        const currentMatches = student.studentMatchesRemaining || 0;
        const newTotal = currentMatches + matchesToAdd;
        
        // Ensure the result is not negative
        const finalTotal = Math.max(0, newTotal);

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${student._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ studentMatchesRemaining: finalTotal })
        });

        let errorData = null;
        if (!response.ok) {
          try {
            errorData = await response.json();
          } catch {
            errorData = { message: `Status ${response.status}` };
          }
        }

        return {
          studentId: student._id,
          studentName: student.name,
          ok: response.ok,
          status: response.status,
          error: errorData?.message || errorData?.error || null
        };
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.ok).length;
      const failedResults = results.filter(r => !r.ok);
      
      if (successCount === selectedStudents.length) {
        alert(`✅ Added ${matchesToAdd} match(es) to ${successCount} student(s)!`);
        setBulkMatchesValue('');
        setSelectedUsers([]);
        // Refresh users list if callback provided
        if (refreshUsers) {
          refreshUsers();
        }
      } else if (successCount > 0) {
        const errorMessages = failedResults.map(r => 
          `${r.studentName}: ${r.error || 'Unknown error'}`
        ).join('\n');
        alert(`⚠️ Matches updated for ${successCount} out of ${selectedStudents.length} student(s).\n\nFailures:\n${errorMessages}`);
        if (refreshUsers) {
          refreshUsers();
        }
        setSelectedUsers([]);
      } else {
        // All failed
        const errorMessages = failedResults.map(r => 
          `${r.studentName}: ${r.error || 'Unknown error'}`
        ).join('\n');
        alert(`❌ Failed to update matches for all students.\n\nErrors:\n${errorMessages}`);
      }
    } catch (error) {
      console.error('Error updating matches:', error);
      alert(`❌ Network error updating matches: ${error.message}`);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Filter Bar */}
      <FilterBar 
        onFiltersChange={setFilters}
        totalUsers={filteredUsers.length}
        users={users}
      />
      
      {/* Header with Search */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-800">
          All Users ({filteredUsers.length})
          {selectedUsers.length > 0 && (
            <span className="ml-2 text-sm font-normal text-blue-600">
              ({selectedUsers.length} selected)
            </span>
          )}
        </h2>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search by name, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.length > 0 && (
        <div className="px-6 py-3 border-b border-blue-200 bg-blue-50 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* Bulk Award Badge */}
            <div className="relative" ref={bulkBadgeRef}>
              <button
                onClick={() => setBulkBadgeDropdownOpen(!bulkBadgeDropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors"
              >
                <Award size={14} />
                Award Badge
              </button>
              
              {bulkBadgeDropdownOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="p-2 max-h-64 overflow-y-auto">
                    {badges.length === 0 ? (
                      <div className="p-2 text-sm text-gray-500">No badges available</div>
                    ) : (
                      badges.map(badge => (
                        <button
                          key={badge._id}
                          onClick={() => handleBulkAwardBadge(badge._id)}
                          className="w-full text-left p-2 hover:bg-gray-100 rounded-md flex items-center gap-2"
                        >
                          <span className="text-lg">{getBadgeIcon(badge.icon)}</span>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{badge.name}</div>
                            <div className="text-xs text-gray-500">{badge.description}</div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Update Matches (students only) */}
            {getSelectedStudents().length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-blue-900 font-medium">
                  Add Matches:
                </label>
                <input
                  type="number"
                  value={bulkMatchesValue}
                  onChange={(e) => setBulkMatchesValue(e.target.value)}
                  placeholder="Amount to add"
                  className="w-28 text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={handleBulkUpdateMatches}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                >
                  Apply
                </button>
              </div>
            )}

            {/* Clear Selection */}
            <button
              onClick={() => setSelectedUsers([])}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100 text-gray-700 text-xs uppercase">
            <tr>
              <th className="px-6 py-3 text-left font-medium tracking-wider w-8">
                <input
                  type="checkbox"
                  checked={allFilteredUsersSelected}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = someFilteredUsersSelected;
                    }
                  }}
                  onChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
              </th>
              <th className="px-6 py-3 text-left font-medium tracking-wider">Name</th>
              <th 
                className="px-6 py-3 text-left font-medium tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center">
                  Joined Platform
                  {getSortIcon('createdAt')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left font-medium tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleSort('lastLoginAt')}
              >
                <div className="flex items-center">
                  Last Login
                  {getSortIcon('lastLoginAt')}
                </div>
              </th>
              <th className="px-6 py-3 text-right font-medium tracking-wider">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <React.Fragment key={user._id}>
                  {/* User Row */}
                  <tr
                    className="hover:bg-gray-50 cursor-pointer transition"
                    onClick={() => toggleRow(user._id)}
                  >
                    <td 
                      className="px-6 py-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => handleUserSelect(user._id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">{user.name}</span>
                        <div className="ml-3">
                          <RoleBadge role={user.role} />
                        </div>
                        <div className="ml-2 text-gray-400">
                          {expandedRow === user._id ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {user.loginHistory && user.loginHistory.length > 0 
                        ? formatDateTime(user.lastLoginAt) 
                        : 'Never'}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center justify-end space-x-3">
                        {user.role === 'student' && (
                          <div
                            className="flex items-center space-x-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <label className="text-xs text-gray-600 whitespace-nowrap">
                              Matches:
                            </label>
                            <input
                              type="number"
                              value={user.studentMatchesRemaining || 0}
                              min="0"
                              onChange={(e) =>
                                updateStudentMatches(user._id, parseInt(e.target.value))
                              }
                              className="w-16 text-xs border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        )}

                        {/* Badge Award Dropdown */}
                        <div 
                          className="relative"
                          ref={el => badgeDropdownRefs.current[user._id] = el}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => setAwardingBadge(awardingBadge?.userId === user._id ? null : { userId: user._id })}
                            className="flex items-center text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 px-2.5 py-1 rounded-md transition-colors"
                          >
                            <Award size={14} className="mr-1" />
                            Award Badge
                          </button>
                          
                          {awardingBadge?.userId === user._id && (
                            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                              <div className="p-2 max-h-64 overflow-y-auto">
                                {badges.length === 0 ? (
                                  <div className="p-2 text-sm text-gray-500">No badges available</div>
                                ) : (
                                  badges.map(badge => (
                                    <button
                                      key={badge._id}
                                      onClick={() => awardBadgeToUser(user._id, badge._id)}
                                      className="w-full text-left p-2 hover:bg-gray-100 rounded-md flex items-center gap-2"
                                    >
                                      <span className="text-lg">{getBadgeIcon(badge.icon)}</span>
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">{badge.name}</div>
                                        <div className="text-xs text-gray-500">{badge.description}</div>
                                      </div>
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteUser(user._id);
                          }}
                          className="flex items-center text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-md transition-colors"
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Details */}
                  {expandedRow === user._id && (
                    <tr>
                      <td colSpan="5" className="bg-gray-50 px-6 py-6 border-t">
                        <div className="grid gap-6 md:grid-cols-2">
                          {/* Basic Info */}
                          <div className="bg-white rounded-lg border p-4 shadow-sm">
                            <h4 className="font-semibold text-gray-800 mb-3">Basic Information</h4>
                            <div className="space-y-2 text-sm text-gray-700">
                              <div>Email: <span className="font-medium text-gray-900">{user.email}</span></div>
                              <div>Gender: {user.gender || 'N/A'}</div>
                              <div>Citizenship: {user.citizenship || 'N/A'}</div>
                              <div>First Language: {user.firstLanguage || 'N/A'}</div>
                              <div>
                                LinkedIn:{' '}
                                {user.linkedinUrl ? (
                                  <a
                                    href={user.linkedinUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    View Profile
                                  </a>
                                ) : (
                                  'N/A'
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Academic Info */}
                          <div className="bg-white rounded-lg border p-4 shadow-sm">
                            <h4 className="font-semibold text-gray-800 mb-3">Academic Information</h4>
                            <div className="space-y-2 text-sm text-gray-700">
                              <div>Fields: {user.fields?.join(', ') || 'N/A'}</div>
                              <div>Interests: {user.interests?.join(', ') || 'N/A'}</div>
                              <div>Preferred Countries: {user.preferredCountries?.join(', ') || 'N/A'}</div>
                              <div>
                                Profile Completed:{' '}
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    user.profileCompleted
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}
                                >
                                  {user.profileCompleted ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Test Scores */}
                          {user.role === 'student' && (
                            <div className="bg-white rounded-lg border p-4 shadow-sm md:col-span-2">
                              <h4 className="font-semibold text-gray-800 mb-3">Test Scores</h4>
                              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-700">
                                <div>
                                  <h5 className="font-medium text-gray-700 mb-1">GRE</h5>
                                  <p>Verbal: {formatTestScore(user.greVerbal)}</p>
                                  <p>Quantitative: {formatTestScore(user.greQuantitative)}</p>
                                  <p>Writing: {formatTestScore(user.greWriting)}</p>
                                </div>
                                <div>
                                  <h5 className="font-medium text-gray-700 mb-1">TOEFL</h5>
                                  <p>Reading: {formatTestScore(user.toeflReading)}</p>
                                  <p>Listening: {formatTestScore(user.toeflListening)}</p>
                                  <p>Speaking: {formatTestScore(user.toeflSpeaking)}</p>
                                  <p>Writing: {formatTestScore(user.toeflWriting)}</p>
                                </div>
                                <div>
                                  <h5 className="font-medium text-gray-700 mb-1">IELTS</h5>
                                  <p>Listening: {formatTestScore(user.ieltsListening)}</p>
                                  <p>Reading: {formatTestScore(user.ieltsReading)}</p>
                                  <p>Speaking: {formatTestScore(user.ieltsSpeaking)}</p>
                                  <p>Writing: {formatTestScore(user.ieltsWriting)}</p>
                                  <p>Overall: {formatTestScore(user.ieltsOverall)}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Matched Positions */}
                          {user.matchedPositions && user.matchedPositions.length > 0 && (
                            <div className="bg-white rounded-lg border p-4 shadow-sm md:col-span-2">
                              <h4 className="font-semibold text-gray-800 mb-3">
                                Matched Positions ({user.matchedPositions.length})
                              </h4>

                              <ul className="divide-y divide-gray-100 text-sm text-gray-700">
                                {user.matchedPositions.map((match, index) => (
                                  <li key={index} className="py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {match.position?.title || 'Untitled Position'}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {match.position?.institution || 'Unknown Institution'}
                                        {match.position?.department ? ` — ${match.position.department}` : ''}
                                      </p>
                                    </div>

                                    {match.final_score !== undefined && (
                                      <span className="mt-1 sm:mt-0 text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                                        Match Score: {match.final_score.toFixed(2)}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* User Badges */}
                          <div className="bg-white rounded-lg border p-4 shadow-sm md:col-span-2">
                            <h4 className="font-semibold text-gray-800 mb-3">
                              Badges ({userBadges[user._id]?.length || 0})
                            </h4>
                            {userBadges[user._id] && userBadges[user._id].length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {userBadges[user._id].map((userBadge) => {
                                  const badge = userBadge.badgeId;
                                  return (
                                    <div
                                      key={userBadge._id}
                                      className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                                      style={{ 
                                        backgroundColor: `${badge?.color || '#3B82F6'}20`,
                                        border: `1px solid ${badge?.color || '#3B82F6'}`
                                      }}
                                    >
                                      <span>{getBadgeIcon(badge?.icon)}</span>
                                      <span className="font-medium">{badge?.name}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No badges awarded yet.</p>
                            )}
                          </div>

                          {/* Referrals - Only for students */}
                          {user.role === 'student' && (
                            <div className="bg-white rounded-lg border p-4 shadow-sm md:col-span-2">
                              <h4 className="font-semibold text-gray-800 mb-3">
                                Successful Referrals ({userReferrals[user._id]?.length || 0})
                              </h4>
                              {userReferrals[user._id] && userReferrals[user._id].length > 0 ? (
                                <div className="space-y-2">
                                  <ul className="divide-y divide-gray-100 text-sm text-gray-700 max-h-64 overflow-y-auto">
                                    {userReferrals[user._id].map((referral) => {
                                      const referredUser = referral.referredUser;
                                      return (
                                        <li key={referral._id} className="py-2">
                                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                            <div>
                                              <div className="font-medium text-gray-900">
                                                {referredUser?.name || 'Unknown User'}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                {referredUser?.email || 'N/A'}
                                              </div>
                                            </div>
                                            <div className="flex flex-col sm:flex-row gap-2 text-xs text-gray-500">
                                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                                {referredUser?.role || 'N/A'}
                                              </span>
                                              {referredUser?.createdAt && (
                                                <span className="px-2 py-1 bg-gray-100 rounded">
                                                  Joined: {formatDate(referredUser.createdAt)}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 py-2">No successful referrals yet.</div>
                              )}
                            </div>
                          )}

                          {/* System Info */}
                          <div className="bg-white rounded-lg border p-4 shadow-sm md:col-span-2">
                            <h4 className="font-semibold text-gray-800 mb-3">System Information</h4>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-gray-700">
                              <div>Created: {formatDate(user.createdAt)}</div>
                              <div>Last Login: {
                                user.loginHistory && user.loginHistory.length > 0 
                                  ? formatDateTime(user.lastLoginAt) 
                                  : 'Never'
                              }</div>
                              <div>Google Linked: {user.googleId ? 'Yes' : 'No'}</div>
                              <div>Resume: {user.resumeKey ? 'Uploaded' : 'None'}</div>
                              <div>Saved Items: {user.savedItems?.length || 0}</div>
                            </div>
                          </div>

                          {/* Login History */}
                          <div className="bg-white rounded-lg border p-4 shadow-sm md:col-span-2">
                            <h4 className="font-semibold text-gray-800 mb-3">
                              Login History ({user.loginHistory?.length || 0})
                            </h4>
                            {user.loginHistory && user.loginHistory.length > 0 ? (
                              <div className="space-y-2">
                                <ul className="divide-y divide-gray-100 text-sm text-gray-700 max-h-64 overflow-y-auto">
                                  {user.loginHistory.map((login, index) => (
                                    <li key={index} className="py-2">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                        <div className="font-medium text-gray-900">
                                          {formatDateTime(login.loginDate)}
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-2 text-xs text-gray-500">
                                          {login.ipAddress && (
                                            <span className="px-2 py-1 bg-gray-100 rounded">
                                              IP: {login.ipAddress}
                                            </span>
                                          )}
                                          {login.userAgent && (
                                            <span 
                                              className="px-2 py-1 bg-gray-100 rounded truncate max-w-xs"
                                              title={login.userAgent}
                                            >
                                              {login.userAgent.length > 50 
                                                ? `${login.userAgent.substring(0, 50)}...` 
                                                : login.userAgent}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 py-2">No login history available</div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                  No users found matching “{searchQuery}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTable;

